import React, { useEffect, useState } from 'react'
import {
  ActivityIndicator,
  Image,
  NativeScrollEvent,
  NativeSyntheticEvent,
  ScrollView,
  StyleSheet,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from 'react-native'
import { useTheme } from '@react-navigation/native'
import { Icon, IconName, SearchBar, Text } from '@components'
import { Contact, usePaginateContacts } from '@hooks'
import { Theme } from '@theme'
import { Scope } from '@utils'
import { defaultImage } from '@assets'
import { useLanguage } from '@context'
import ContactsFilter from '../../components/ContactsFilter'

interface NotePersonPickerProps {
  selectedContactId?: string | null
  // « dropdown »: the boxed list under the field, on the computer.
  // « sheet »: the same list filling the phone's bottom sheet.
  variant?: 'dropdown' | 'sheet'
  onSelect: (contact: Contact) => void
  // Offered above the list while a person is chosen: a note about nobody again.
  onRemove?: () => void
}

// How far from the bottom of the list the next page starts loading.
const LOAD_MORE_THRESHOLD = 60
// Enough profiles at once to fill the list, so that it scrolls and loads the
// following ones (ten of them left it too short to scroll).
const PAGE_SIZE = 30
// The boxed list of the computer: 380 px at most, less in a short window so
// that it never runs past the screen (the search, the chips and what surrounds
// the list take about 400 px).
const DROPDOWN_LIST_MAX_HEIGHT = 380
const DROPDOWN_LIST_MIN_HEIGHT = 160
const DROPDOWN_SURROUNDINGS = 400
// The lists follow the search a moment after the last keystroke.
const SEARCH_DELAY_MS = 250

const scopeIcon = (contact: Contact): IconName | null => {
  if (contact.isGhost || contact.scope === Scope.GHOST) return 'ghost'
  if (contact.scope === Scope.PROFESSIONAL) return 'work'
  if (contact.scope === Scope.PRIVATE) return 'lock'
  return null
}

// « Choisir une personne » of the note editor: a search, the app's Tous /
// Privé / Pro / fantôme chips and every profile. « Tous » is on by default, so
// a Pro contact shows up as readily as a private one.
export const NotePersonPicker = ({
  selectedContactId,
  variant = 'dropdown',
  onSelect,
  onRemove,
}: NotePersonPickerProps) => {
  const theme = useTheme()
  const { t } = useLanguage()
  const styles = getStyles(theme)
  const { height: windowHeight } = useWindowDimensions()
  const isSheet = variant === 'sheet'
  const [typed, setTyped] = useState('')
  const [search, setSearch] = useState('')
  const [scopes, setScopes] = useState<Scope[]>([])
  useEffect(() => {
    const timer = setTimeout(() => setSearch(typed), SEARCH_DELAY_MS)
    return () => clearTimeout(timer)
  }, [typed])

  // Same chips as the contact column of the Notes screen.
  const hasGhost = scopes.includes(Scope.GHOST)
  const hasPrivate = scopes.includes(Scope.PRIVATE)
  const hasProfessional = scopes.includes(Scope.PROFESSIONAL)
  const scope = hasPrivate ? Scope.PRIVATE : hasProfessional ? Scope.PROFESSIONAL : undefined
  const isAll = scopes.length === 0
  // Two lists, the real contacts and then the ghost profiles, each one loaded
  // page after page. Asked for together, the server pages them wrongly: with
  // « Tous », everyone after the first page could not be reached by scrolling.
  const people = usePaginateContacts(
    { scope, search, noGhost: true, limit: PAGE_SIZE },
    { enabled: !hasGhost },
  )
  const ghosts = usePaginateContacts(
    { scope, search, onlyGhost: true, limit: PAGE_SIZE },
    { enabled: hasGhost || isAll },
  )
  // Under « Tous » the ghost profiles show once every contact is there: shown
  // earlier, the following contacts would slip in above them.
  const hasEveryContact = !people.isPending && !people.hasNextPage
  const contacts = hasGhost
    ? ghosts.flatData
    : [...people.flatData, ...(isAll && hasEveryContact ? ghosts.flatData : [])]
  const totalCount = hasGhost
    ? ghosts.totalCount
    : people.totalCount + (isAll ? ghosts.totalCount : 0)
  const isFetching = people.isFetching || ghosts.isFetching
  const isFetchingNextPage = people.isFetchingNextPage || ghosts.isFetchingNextPage

  const heading = hasGhost
    ? t('notesForm.ghostCount', { count: totalCount })
    : hasPrivate
      ? t('notesForm.privateCount', { count: totalCount })
      : hasProfessional
        ? t('notesForm.proCount', { count: totalCount })
        : t('notesForm.allProfilesCount', { count: totalCount })

  // The contacts first; the ghost profiles follow once they are all there.
  const loadMore = () => {
    if (isFetchingNextPage) return
    if (!hasGhost && people.hasNextPage) void people.fetchNextPage()
    else if ((hasGhost || isAll) && ghosts.hasNextPage) void ghosts.fetchNextPage()
  }

  const onScroll = ({ nativeEvent }: NativeSyntheticEvent<NativeScrollEvent>) => {
    const { layoutMeasurement, contentOffset, contentSize } = nativeEvent
    const nearBottom =
      layoutMeasurement.height + contentOffset.y >= contentSize.height - LOAD_MORE_THRESHOLD
    if (nearBottom) loadMore()
  }

  const dropdownListHeight = Math.max(
    DROPDOWN_LIST_MIN_HEIGHT,
    Math.min(DROPDOWN_LIST_MAX_HEIGHT, windowHeight - DROPDOWN_SURROUNDINGS),
  )

  const renderPerson = (contact: Contact) => {
    const isSelected = contact.id === selectedContactId
    const icon = scopeIcon(contact)
    return (
      <TouchableOpacity
        key={contact.id}
        testID={`note-person-${contact.id}`}
        style={[styles.person, isSelected && styles.personSelected]}
        onPress={() => onSelect(contact)}
      >
        <Image
          source={contact.avatarUrl ? { uri: contact.avatarUrl } : defaultImage}
          style={styles.avatar}
        />
        <View style={styles.identity}>
          <Text type="bodySmallBold" numberOfLines={1}>
            {contact.fullName}
          </Text>
          {!!contact.hashtags?.length && (
            <Text type="bodyExtraSmall" color={theme.colors.primary} numberOfLines={1}>
              {contact.hashtags.join(' ')}
            </Text>
          )}
        </View>
        {isSelected ? (
          <Icon name="check" size={18} color={theme.colors.primary} />
        ) : (
          icon && <Icon name={icon} size={15} color={theme.colors.greyBlue} />
        )}
      </TouchableOpacity>
    )
  }

  return (
    <View testID="note-person-picker" style={isSheet ? styles.sheet : styles.container}>
      {/* On the phone the keyboard only opens when the search is tapped, so the
          profiles stay in view. */}
      <SearchBar value={typed} onChangeText={setTyped} autoFocus={!isSheet} />
      <View style={styles.chips}>
        <ContactsFilter
          scope={scopes}
          onScopeChange={setScopes}
          allLabel={t('notesForm.allProfiles')}
        />
      </View>
      <Text
        testID="note-person-heading"
        type="bodyExtraSmallBold"
        color={theme.colors.greyBlue}
        style={styles.heading}
      >
        {heading}
      </Text>
      <ScrollView
        style={isSheet ? styles.sheetList : { maxHeight: dropdownListHeight }}
        onScroll={onScroll}
        scrollEventThrottle={100}
        keyboardShouldPersistTaps="handled"
      >
        {!!selectedContactId && !!onRemove && (
          <TouchableOpacity testID="note-person-remove" style={styles.person} onPress={onRemove}>
            <View style={styles.removeIcon}>
              <Icon name="cross" size={16} color={theme.colors.greyBlue} />
            </View>
            <Text type="bodySmall" color={theme.colors.greyBlue} style={styles.identity}>
              {t('notesForm.removePerson')}
            </Text>
          </TouchableOpacity>
        )}
        {contacts.map(renderPerson)}
        {!isFetching && contacts.length === 0 && (
          <Text type="bodySmall" color={theme.colors.greyBlue} style={styles.empty}>
            {t('notesForm.noProfileFound')}
          </Text>
        )}
        {(isFetchingNextPage || (isFetching && contacts.length === 0)) && (
          <ActivityIndicator size="small" color={theme.colors.primary} style={styles.loader} />
        )}
      </ScrollView>
    </View>
  )
}

const getStyles = (theme: Theme) =>
  StyleSheet.create({
    container: {
      backgroundColor: theme.colors.white,
      borderWidth: 1,
      borderColor: theme.colors.grey20,
      borderRadius: theme.border.medium,
      padding: 10,
      shadowColor: theme.colors.black,
      shadowOpacity: 0.14,
      shadowRadius: 24,
      shadowOffset: { width: 0, height: 8 },
      elevation: 8,
    },
    sheet: {
      flex: 1,
      width: '100%',
    },
    sheetList: {
      flex: 1,
    },
    chips: {
      marginTop: 10,
    },
    heading: {
      marginTop: theme.spacing.md,
      marginBottom: theme.spacing.xs,
      marginHorizontal: theme.spacing.sm,
    },
    person: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 10,
      paddingVertical: 7,
      paddingHorizontal: theme.spacing.sm,
      borderRadius: 10,
    },
    personSelected: {
      backgroundColor: theme.colors.primary + '12',
    },
    avatar: {
      width: 32,
      height: 32,
      borderRadius: 16,
    },
    removeIcon: {
      width: 32,
      height: 32,
      borderRadius: 16,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: theme.colors.grey10,
    },
    identity: {
      flex: 1,
      minWidth: 0,
    },
    empty: {
      paddingVertical: theme.spacing.md,
      paddingHorizontal: theme.spacing.sm,
    },
    loader: {
      paddingVertical: theme.spacing.sm,
    },
  })
